import { Card } from "@/components/ui/card"
import { Clock, GripVertical, Star } from "lucide-react"
import { intervalToDuration } from "date-fns"
import { formatDuration } from "@/shared/utils/date-fn"
import type { Activity } from "../../../entities/activities"
import type { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities"
import type { DraggableAttributes } from "@dnd-kit/core"
import type { FlashcardIdPageState } from "@/pages/activities/flashcards/id"
import type { DiagnosticIdPageState } from "@/pages/activities/diagnostic/id"
import type { ChallengeIdPageState } from "@/pages/activities/challenge/id"
import type { LessonIdPageState } from "@/pages/activities/lessons/id"
import { Chip } from "@/components/ui/chip"
import { convertToSeconds } from "@/shared/utils/time"
import { useNavigate } from "react-router-dom"
import { Checkbox } from "@/components/ui/checkbox"
import { cn } from "@/lib/utils"
import { getDraggableDay } from "@/modules/study/utils/time"
import { useActivitiesFilter } from "@/modules/study/stores/use-activities-filter-store"
import useCoursesInfo from "@/modules/courses/hooks/use-courses-info"
import { useAuth } from "@/modules/auth/store"
import { UserRole } from "@/shared/entities/users"
import { useState } from "react"
import useUpdateActivity from "@/modules/study/queries/use-update-activity"
import { toast } from "sonner"
import mixpanelClient from "@/services/mixpanel"
import { useCosmos } from "@/modules/cosmos/stores/use-cosmos-store"

const getBasicActivityType = (subType: string) => {
  switch (subType) {
    case "lecture":
      return {
        icon: "📖",
        label: "Lectura",
      }
    case "video":
      return {
        icon: "🎥",
        label: "Video",
      }
    case "homework":
      return {
        icon: "📝",
        label: "Tarea",
      }
    default:
      return {
        icon: "",
        label: "",
      }
  }
}

export interface ActivitiesWeeklyCardProps extends Activity {
  dragListeners?: SyntheticListenerMap
  dragAttributes?: DraggableAttributes
  isPending?: boolean
  canHideCard?: boolean
  isDailyCalendar?: boolean
}

export default function ActivitiesWeeklyCard({
  type,
  name,
  subject,
  status,
  estimatedDuration,
  activityId,
  dragListeners,
  dragAttributes,
  dateTime,
  id,
  isPending,
  canHideCard,
  userWeeklyObjectiveId,
  isDailyCalendar = false,
  basic,
}: ActivitiesWeeklyCardProps) {
  const { user } = useAuth()
  const { getCourseColor } = useCoursesInfo()
  const navigate = useNavigate()
  const { filteredSubjects } = useActivitiesFilter()
  const effortBased = ["lesson", "challenge"]
  const isApproved = status === "approved"
  const isSkipped = status === "skipped"
  const color = getCourseColor(subject.id)
  const duration = intervalToDuration({ start: 0, end: convertToSeconds(estimatedDuration) * 1000 })
  const formattedDuration = formatDuration(duration).min()
  const activityEvaluationType = effortBased.includes(type) ? "💪" : "🎯"
  const isDraggableDay = getDraggableDay(dateTime)
  const cannotContinue =
    user?.role !== UserRole.SUPERVISOR &&
    (status === "approved" || (!isDraggableDay && status !== "skipped")) &&
    type !== "basic"
  const canShowCard =
    filteredSubjects.some((filtered) => filtered.id === subject.id) || isDailyCalendar

  const basicActEvType = type === "basic" ? getBasicActivityType(basic?.subType ?? "").icon : ""
  const [isTooltipVisible, setIsTooltipVisible] = useState(false)
  const updateActivity = useUpdateActivity()
  const { cosmosKey } = useCosmos()

  const activityType = {
    flashcards: "Flashcard",
    diagnostics: "Diagnóstico",
    lessons: "Lecciones",
    challenges: "Desafio",
    basic: getBasicActivityType(basic?.subType ?? "").label,
  }[type]

  const activityStatus = {
    pending: {
      icon: null,
      text: "",
    },
    failed: {
      icon: <Clock className="h-3 w-3 self-center" />,
      text: "Reintentá para mejorar tu resultado",
    },
    rescheduled: {
      icon: null,
      text: "",
    },
    approved: {
      icon: <Checkbox className="h-4 w-4 self-center" checked />,
      text: "Realizada",
    },
    skipped: {
      icon: <Star className="h-4 w-4 self-center rounded bg-[#EAB308] text-white shadow" />,
      text: "Ahorrada",
    },
  }[status]

  const handleOnActivityClick = () => {
    if (cannotContinue) return

    if (type === "basic") {
      window.open(basic?.externalUrl, "_blank")
      return
    }

    mixpanelClient.activities.start({
      name: user.firstName,
      email: user.emailAddress,
      subject: subject.name,
      prevStatus: status,
      startDate: new Date().toISOString(),
      type,
      organization: cosmosKey,
    })

    switch (type) {
      case "flashcards": {
        const state: FlashcardIdPageState = {
          name: name,
          course: subject.name,
          id,
          userWeeklyObjectiveId,
        }

        navigate(`/activities/flashcards/${activityId}`, {
          state,
        })

        break
      }
      case "diagnostics": {
        const state: DiagnosticIdPageState = {
          name: name,
          course: subject.name,
          id,
          userWeeklyObjectiveId,
        }

        navigate(`/activities/diagnostic/${activityId}`, {
          state,
        })
        break
      }
      case "challenges": {
        const state: ChallengeIdPageState = {
          name: name,
          course: subject.name,
          id,
        }

        navigate(`/activities/challenge/${activityId}`, {
          state,
        })
        break
      }
      case "lessons": {
        const state: LessonIdPageState = {
          name: name,
          course: subject.name,
          id,
        }

        navigate(`/activities/lessons/${activityId}`, {
          state,
        })
        break
      }
    }

    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const handleOnActivityBasicClick = async () => {
    if (cannotContinue) return

    try {
      await updateActivity.mutateAsync({
        id,
        status: status === "approved" ? "pending" : "approved",
      })
    } catch (_) {
      toast.error("Error al marcar la actividad como realizada")
    }
  }

  return canShowCard ? (
    <Card
      className={cn(
        "relative z-[6] w-full select-none hover:cursor-pointer hover:bg-primary-50",
        canHideCard && "bg-transparent opacity-0",
        cannotContinue ? "!cursor-not-allowed hover:cursor-not-allowed" : "cursor-pointer",
        isPending && "pointer-events-none opacity-60",
      )}
      onClick={handleOnActivityClick}
      onMouseEnter={() => {
        if (status === "pending" || status === "rescheduled") {
          setIsTooltipVisible(true)
        }
      }}
      onMouseLeave={() => setIsTooltipVisible(false)}
    >
      {type === "basic" && isTooltipVisible ? (
        <div className="absolute -left-0 -top-[50%] z-[7] flex h-auto w-full cursor-default flex-col items-start justify-start rounded-lg border border-gray-200 bg-white p-2 shadow-md">
          <p className="text-xs font-semibold">Esta actividad es externa.</p>
          <p className="text-xs">Cuando la termines, acordate de marcarla como realizada.</p>
        </div>
      ) : null}

      <div
        className="absolute h-full w-1 rounded-bl-lg rounded-tl-lg"
        style={{ backgroundColor: isApproved || isSkipped ? "gray" : color }}
      />
      <div className={cn("px-4 py-2", isDailyCalendar && "w-full overflow-hidden")}>
        <div
          className={cn(
            "flex items-start justify-between gap-4",
            isDailyCalendar && "w-full overflow-hidden",
          )}
        >
          <div className={cn("flex-1 space-y-1", isDailyCalendar && "w-full overflow-hidden")}>
            <div
              className={cn(
                (isApproved || isSkipped) && "grayscale",
                isDailyCalendar && "w-full overflow-hidden",
              )}
            >
              <div className="mb-2 flex justify-between">
                <p className="text-xs font-medium text-muted-foreground">{`${type === "basic" ? basicActEvType : activityEvaluationType} ${activityType}`}</p>
                <div className="relative left-2 flex items-center gap-1">
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3 text-neutral-600" />
                    <p className="text-xs text-neutral-600">{formattedDuration}</p>
                  </div>
                  {!isApproved && !isSkipped && !isDailyCalendar ? (
                    <div {...dragAttributes} {...dragListeners}>
                      <GripVertical
                        className={cn(
                          "h-4 w-4 text-neutral-400",
                          cannotContinue
                            ? "!cursor-not-allowed hover:cursor-not-allowed"
                            : "cursor-pointer",
                        )}
                      />
                    </div>
                  ) : (
                    <div className="px-2" />
                  )}
                </div>
              </div>
              <p
                className={cn(
                  "px-1 py-1 text-sm font-medium",
                  isApproved || isSkipped ? "text-neutral-700 line-through" : "text-foreground",
                  isDailyCalendar && "truncate",
                )}
              >
                {name}
              </p>
              <div className="flex py-1">
                <Chip color={color} size="sm">
                  {subject.name}
                </Chip>
              </div>
            </div>
            <div
              style={{ display: activityStatus.icon || activityStatus.text ? "flex" : "none" }}
              className="flex items-center gap-1 py-1"
            >
              {activityStatus.icon}
              <p className="font-regular text-xs">{activityStatus.text}</p>
            </div>

            {type === "basic" && !updateActivity.isPending && status !== "approved" && (
              <div
                className="flex w-fit items-center justify-center gap-1"
                onClick={(e) => {
                  e.stopPropagation()
                  handleOnActivityBasicClick()
                }}
              >
                <Checkbox checked={isApproved} />
                <span className="text-xs">Realizada</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  ) : null
}
